import { Box, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLoaderData } from "react-router-dom";
import { useCurrency } from "../../context/CurrencyContext";
import { usePortfolio } from "../../context/PortfolioContext";
import {
  claimValueByCase,
  claimValueOf,
  withClaimNorm,
} from "../../shared/claimValue";
import { percentage } from "../../shared/percentage";
import { SettlementPortfolio } from "../../shared/PortfolioTypes";
import { captureOf, hasRange } from "../../shared/settlementCapture";
import { twoRealities } from "../../shared/settlementRealities";
import { Metric } from "../general/DataAtoms.tsx";
import { NO_VALUE } from "../general/dataAtoms/shared.ts";
import { SegmentedControl } from "../general/SegmentedControl.tsx";
import { SettlementCostSaved } from "./SettlementCostSaved.tsx";
import {
  SettledVsLitigatedMetric,
  SettlementNetResultChart,
} from "./SettlementNetResultChart.tsx";
import { SectionHeading } from "./PortfolioSection.tsx";
import { SettlementPanel } from "./SettlementPanel.tsx";
import { SettlementQualityChart } from "./SettlementQualityChart.tsx";
import { SettlementRealities } from "./SettlementRealities.tsx";
import { SettlementRiskRewardChart } from "./SettlementRiskRewardChart.tsx";

// the differences the settled-vs-litigated scatter can place its cases by, in
// the order its control offers them
const METRICS: readonly SettledVsLitigatedMetric[] = ["exposure", "netResult"];

/**
 * The settlements of the portfolio: how many there are and what they removed,
 * how good they were, what cost they saved, and where the clients and the firm
 * stand with them against the same cases fought to the end. The layout of the
 * portfolio puts the title of the page and the filters over it.
 */
const SettlementsPortfolio = () => {
  const { currency } = useCurrency();
  const { t } = useTranslation(undefined, {
    keyPrefix: "portfolio.settlement",
  });
  // which difference the settled-vs-litigated scatter plots. Risk leads: it is
  // what settling is for, and the net result is the price paid for it.
  const [metric, setMetric] = useState<SettledVsLitigatedMetric>("exposure");
  const {
    numberOfCases,
    numberOfSettledCases,
    totalAcceptedSettlements,
    totalSettledClaims,
    totalSettledOpponents,
    settlementPaymentsToParty,
    settlementPaymentsByParty,
    clients,
  } = usePortfolio();

  // the per-item settlement data comes from this route's own loader
  // (getSettlementPortfolio), kept off the main portfolio payload
  const { records, realities } = useLoaderData<SettlementPortfolio>();

  // The portfolio as it actually resolved against the same cases fought to the
  // end. Both the cost-saved and the two-realities panels read off this one
  // aggregate, so it is computed here, once.
  const twoWorlds = useMemo(() => twoRealities(realities), [realities]);

  // one dot per settlement for the quality scatter: x = where the settlement
  // landed in the rational range (0 = our EV, 0.5 = midpoint, 1 = opponent's EV);
  // y = claim size. Claim value comes from the portfolio stats already in memory
  // (the per-case quantum the Claimed-amounts section shows). A record with no
  // range to place its settlement in is left out (see hasRange).
  const qualityPoints = useMemo(
    () =>
      withClaimNorm(
        records.filter(hasRange).map((r) => {
          const partyStats = clients.stats[r.partyId]?.[r.caseId];
          return {
            caseId: r.caseId,
            caseName: r.caseName,
            opponentName: r.opponentName,
            capture: captureOf(r),
            settledFigure: r.settledValue,
            partyEv: r.partyEv,
            // the record's opponentEv is the opponent's EV negated, to the
            // client's side: the end of the opponent's bargaining range
            opponentRangeEnd: r.opponentEv,
            mid: r.mid,
            claimValue: partyStats ? claimValueOf(partyStats) : 0,
          };
        }),
      ),
    [records, clients],
  );

  // the stats are kept by party, and a case's snapshot names no party
  const claimByCase = useMemo(() => claimValueByCase(clients.stats), [clients]);

  // one dot per settled case for the net-result difference scatter: x = the
  // change in expected net from settling (settled − litigated), y = claim size,
  // the largest among the case's client parties.
  const netResultPoints = useMemo(
    () =>
      withClaimNorm(
        realities.map((r) => ({
          caseId: r.caseId,
          caseName: r.caseName,
          diff: r.actual.netResult.average - r.litigated.netResult.average,
          before: {
            worst: r.litigated.netResult.worst,
            avg: r.litigated.netResult.average,
            best: r.litigated.netResult.best,
          },
          after: {
            worst: r.actual.netResult.worst,
            avg: r.actual.netResult.average,
            best: r.actual.netResult.best,
          },
          claimValue: claimByCase.get(r.caseId) ?? 0,
        })),
      ),
    [realities, claimByCase],
  );

  // Adverse risk removed, per case rather than per settlement. A case settled
  // with several opponents produces several settlement records whose per-offer
  // deltas don't add up — settlements interact on shared quanta — so the
  // whole-case comparison is the only figure that totals honestly.
  const adverseRisk = useMemo(() => {
    const total = realities.reduce((sum, r) => sum + r.exposureRemoved, 0);
    return { total, avg: total / (realities.length || 1) };
  }, [realities]);

  // the risk/reward trade per case: exposure removed (worst-case lift) vs. change
  // in expected net, plus what the tooltip needs to describe the worst outcome
  const riskRewardPoints = useMemo(
    () =>
      realities.map((r) => ({
        caseId: r.caseId,
        caseName: r.caseName,
        exposureRemoved: r.exposureRemoved,
        evChange: r.actual.netResult.average - r.litigated.netResult.average,
        // a certain settled result has its best and worst outcome equal
        settledCertain: r.actual.netResult.best === r.actual.netResult.worst,
        litigatedWorstProbability: r.litigated.worstProbability,
      })),
    [realities],
  );

  if (totalAcceptedSettlements === 0) {
    return (
      <Box>
        <SectionHeading id="overview">{t("title.none")}</SectionHeading>
        <Typography
          variant="body2"
          sx={{ mt: 0.5, color: "text.secondary", maxWidth: "76ch" }}
        >
          {t("emptyHint")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2.25,
        userSelect: "none",
      }}
    >
      {/* ---- Overview: the settlements in one sentence, which is the heading
              of the section ---- */}
      <SectionHeading id="overview">
        {t("title.summary", {
          total: totalAcceptedSettlements,
          claims: totalSettledClaims,
          opponents: totalSettledOpponents,
        })}
      </SectionHeading>
      <SettlementPanel
        label={t("overview.title")}
        description={t("overview.description")}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
            columnGap: 3.5,
            rowGap: 2.25,
            mt: 2,
          }}
        >
          <Metric
            label={t("overview.settledCases.title")}
            value={String(numberOfSettledCases)}
            suffix={t("overview.settledCases.ofTotal", {
              total: numberOfCases,
            })}
            caption={t("overview.settledCases.subTitle", {
              rate: percentage.format(
                numberOfCases > 0 ? numberOfSettledCases / numberOfCases : 0,
              ),
            })}
          />
          <Metric
            label={t("overview.received.title")}
            value={currency.format(settlementPaymentsToParty.total)}
            caption={t("overview.received.subTitle", {
              avg: currency.format(settlementPaymentsToParty.avg),
            })}
          />
          {settlementPaymentsByParty.total > 0 && (
            <Metric
              label={t("overview.paid.title")}
              value={currency.format(settlementPaymentsByParty.total)}
              caption={t("overview.paid.subTitle", {
                avg: currency.format(settlementPaymentsByParty.avg),
              })}
            />
          )}
          <Metric
            label={t("overview.exposure.title")}
            // no litigated baseline yet is not the same as no risk removed: shown
            // as unavailable, like the panels below that need the baseline too
            value={
              realities.length > 0
                ? currency.format(adverseRisk.total)
                : NO_VALUE
            }
            caption={
              realities.length > 0
                ? t("overview.exposure.subTitle", {
                    avg: currency.format(adverseRisk.avg),
                  })
                : t("overview.exposure.noBaseline")
            }
          />
        </Box>
      </SettlementPanel>

      {/* ---- Settlement quality: what settling traded, how well we bargained,
              and how the two worlds compare ---- */}
      <SectionHeading id="quality" rule>
        {t("sectionTitle")}
      </SectionHeading>

      <SettlementPanel
        label={t("riskReward.title")}
        description={t("riskReward.description")}
      >
        <SettlementRiskRewardChart
          title={t("riskReward.chartTitle")}
          subtitle={t("riskReward.chartSubtitle")}
          points={riskRewardPoints}
          emptyMessage={t("noBaseline")}
        />
      </SettlementPanel>

      <SettlementPanel
        label={t("quality.title")}
        description={t("quality.description")}
      >
        <SettlementQualityChart
          title={t("quality.chartTitle")}
          subtitle={t("quality.chartSubtitle")}
          points={qualityPoints}
          emptyMessage={t("quality.empty")}
        />
      </SettlementPanel>

      {/* both views are the same scatter over the same cases — only the
          difference being measured changes, so they share a control */}
      <SettlementPanel
        id="settledVsLitigated"
        label={t("settledVsLitigated.title")}
        description={t("settledVsLitigated.description")}
        control={
          <SegmentedControl
            value={metric}
            options={METRICS.map((value) => ({
              value,
              label: t(`settledVsLitigated.${value}.toggle`),
            }))}
            onChange={setMetric}
            ariaLabel={t("settledVsLitigated.toggleLabel")}
          />
        }
      >
        <SettlementNetResultChart
          title={t(`settledVsLitigated.${metric}.chartTitle`)}
          subtitle={t(`settledVsLitigated.${metric}.chartSubtitle`)}
          points={netResultPoints}
          metric={metric}
          emptyMessage={t("noBaseline")}
        />
      </SettlementPanel>

      {/* ---- Litigation cost avoided ---- */}
      <SectionHeading id="savings" rule>
        {t("savings.title")}
      </SectionHeading>
      <SettlementCostSaved realities={realities} twoWorlds={twoWorlds} />

      {/* ---- Two realities: settling vs. not settling (client & firm) ---- */}
      <SettlementRealities realities={realities} twoWorlds={twoWorlds} />
    </Box>
  );
};

export default SettlementsPortfolio;
